import { FC, useEffect, useRef } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import formatQuestions from "src/containers/EditObservationQuestionsForrm/formatQuestions";
import { AdminObservationQuestion } from "src/features/observationsAdmin/components";
import useAdminForm from "src/hooks/admin/useAdminForm/useAdminForm";
import AdminObservationHeading from "../../components/AdminObservationHeading/AdminObservationHeading";
import {
	DEFAULT_ADMIN_OBSERVATION_ANSWERS,
	DEFAULT_ADMIN_OBSERVATION_HEADING,
	DEFAULT_ADMIN_OBSERVATION_QUESTION
} from "../../constants";
import {
	useCleanObservationForm,
	useCreateObservationQuestion,
	useDeleteObservationQuestion,
	usePatchObservationForm
} from "../../hooks";
import usePatchObservationQuestion from "../../hooks/usePatchObservationQuestion/usePatchObservationQuestion";
import { QuestionTypeMenuButton } from "src/features/Admin/Observations/components/QuestionTypeMenuButton";

interface Props {
	observationFormId: number;
	formName?: string;
	onBranchedQuestionsPreviewOpen?: () => void;
}

const AdminObservationQuestionList: FC<Props> = ({
	observationFormId,
	onBranchedQuestionsPreviewOpen
}) => {
	const lastQuestionRef = useRef<HTMLDivElement | null>(null);

	const { refetch } = useAdminForm(observationFormId, {
		enabled: false
	});

	const { mutate: updateQuestionOrder } = usePatchObservationQuestion();

	const { mutateAsync: cleanObservationForm } = useCleanObservationForm();

	const { control, setValue } = useFormContext<AdminObservationForm>();

	const { fields, append, remove, swap } = useFieldArray({
		control,
		name: "questions"
	});

	const scoreThreshold = useWatch({
		control,
		name: "observationScoreThreshold"
	});

	const parseScoreThreshold = parseInt(scoreThreshold as any); // useWatch is returning a string instead of number

	const { mutate: deleteObservationQuestion } = useDeleteObservationQuestion();
	const { mutateAsync: createObservationQuestion } =
		useCreateObservationQuestion();

	const questions = useWatch({ control, name: "questions" }) ?? [];

	const selectedQuestionIndex = useWatch({
		control,
		name: "selectedQuestionIndex"
	});

	const canBeRateQuestions = questions.filter(
		(question) => question.type === "RADIO" || question.type === "SELECT"
	);

	const hasUnansweredQuestions = questions.some(
		(question) => !question.id || question.question === ""
	);

	const { mutate: submitScoreThreshold } = usePatchObservationForm();

	useEffect(() => {
		if (canBeRateQuestions.length || scoreThreshold) return;

		setValue("observationScoreThreshold", undefined);
	}, [questions]);

	const handleRemoveQuestion = (questionIndex: number) => {
		const questionId = questions[questionIndex].id;

		if (!questionId) {
			remove(questionIndex);

			// remove junk data
			const sanitized = questions.filter((question) => question.id);

			setValue("questions", sanitized);
			return;
		}

		deleteObservationQuestion(questionId, {
			onSuccess: async () => {
				await cleanObservationForm(observationFormId);

				const { data: observationForm } = await refetch();

				remove(questionIndex);
				setValue("questions", formatQuestions(observationForm) as any);
			}
		});

		if (parseScoreThreshold === 1 && canBeRateQuestions.length === 1) {
			setValue("observationScoreThreshold", undefined);

			submitScoreThreshold({
				id: observationFormId,
				score_threshold: null
			});

			return;
		}

		if (parseScoreThreshold === canBeRateQuestions.length) {
			const newScoreThreshold = parseScoreThreshold - 1;

			setValue("observationScoreThreshold", newScoreThreshold);

			submitScoreThreshold({
				id: observationFormId,
				score_threshold: newScoreThreshold
			});
		}
	};

	const handleMoveQuestionDown = (
		index: number,
		answerOne: number | null,
		answerTwo: number | null
	) => {
		// answerOne is the question id relating to the first question to move
		// answerTwo is the question id of the next/previous question relative +/- 1 to answerOne

		if (!answerOne || !answerTwo) return;

		const order = index + 1;

		setValue(`questions.${index}.order`, order + 1);
		setValue(`questions.${index + 1}.order`, order);

		updateQuestionOrder({
			id: answerOne,
			order: order + 1
		});

		updateQuestionOrder({ id: answerTwo, order });

		swap(index, index + 1);
	};

	const handleMoveQuestionUp = (
		index: number,
		answerOne: number | null,
		answerTwo: number | null
	) => {
		// answerOne is the question id relating to the first question to move
		// answerTwo is the question id of the next/previous question relative +/- 1 to answerOne
		if (!answerOne || !answerTwo) return;

		const order = index + 1;

		setValue(`questions.${index}.order`, order - 1);
		setValue(`questions.${index - 1}.order`, order);

		updateQuestionOrder({
			id: answerOne,
			order: order - 1
		});

		updateQuestionOrder({ id: answerTwo, order });

		swap(index, index - 1);
	};

	const handleMenuItemClick = async (type: string, questionTypeId: number) => {
		const order = questions.length + 1;

		if (type === "OBSERVATION_DATE") {
			setValue("hasObservationDateQuestion", true);
		}

		if (type === "CC_EMAIL_RECIPIENT") {
			setValue("hasCCEmailQuestion", true);
		}

		try {
			const createdQuestion = await createObservationQuestion({
				observation_form_id: observationFormId,
				question_type_id: questionTypeId,
				order: questions.length + 1,
				question: "",
				note: "",
				is_correct_answer_required: false
			});

			if (type === "HEADING") {
				append({
					...DEFAULT_ADMIN_OBSERVATION_HEADING,
					order,
					id: createdQuestion.id
				});
			} else if (type === "RADIO" || type === "SELECT") {
				append({
					...DEFAULT_ADMIN_OBSERVATION_QUESTION,
					answers: DEFAULT_ADMIN_OBSERVATION_ANSWERS,
					id: createdQuestion.id,
					order,
					type
				});

				lastQuestionRef.current?.scrollIntoView({
					behavior: "smooth"
				});
			} else {
				append({
					...DEFAULT_ADMIN_OBSERVATION_QUESTION,
					id: createdQuestion.id,
					order,
					type
				});
				lastQuestionRef.current?.scrollIntoView({
					behavior: "smooth"
				});
			}
		} catch (error) {
			console.error("Error creating question", error);
		}

		setValue("selectedQuestionIndex", questions.length);
	};

	const handleQuestionClick = (index: number) => {
		// Set selected question index
		setValue("selectedQuestionIndex", index);
	};

	// FIXME: Ideally, we want to use !isValid here
	// We cant because pass rate is required but we don't want to disable the menu button
	// if the user hasn't answered pass rate
	// Any other required fields will need to update this logic below
	const isMenuButtonDisabled = hasUnansweredQuestions;

	return (
		<div>
			{fields?.map((field, index) => (
				<div
					key={field.id}
					className={`mb-2 p-4 rounded-lg cursor-pointer border-2 ${selectedQuestionIndex === index ? "border-foreground" : "border-transparent"}`}
					ref={index === fields.length - 1 ? lastQuestionRef : null}
					onClick={() => handleQuestionClick(index)}
				>
					{field.type === "HEADING" && (
						<AdminObservationHeading
							questionIndex={index}
							isQuestionActionsVisible={selectedQuestionIndex === index}
							removeQuestion={() => handleRemoveQuestion(index)}
							moveQuestionDown={handleMoveQuestionDown}
							moveQuestionUp={handleMoveQuestionUp}
							{...field}
						/>
					)}

					{field.type !== "HEADING" && (
						<AdminObservationQuestion
							questionIndex={index}
							isQuestionActionsVisible={selectedQuestionIndex === index}
							removeQuestion={() => handleRemoveQuestion(index)}
							moveQuestionDown={handleMoveQuestionDown}
							moveQuestionUp={handleMoveQuestionUp}
							questionType={field.type}
							onBranchedQuestionsPreviewOpen={onBranchedQuestionsPreviewOpen}
							{...field}
						/>
					)}
				</div>
			))}

			<div className={fields.length === 0 ? "mt-48" : "mt-10"}>
				<QuestionTypeMenuButton
					onMenuItemClick={handleMenuItemClick}
					isDisabled={isMenuButtonDisabled}
				/>
			</div>
		</div>
	);
};

export default AdminObservationQuestionList;
